// Lo que el vendedor DIGITA en una nota antes de que el backend la calcule. Aquí no hay precios,
// escalas, impuestos ni totales: eso lo devuelve la API (previsualizar / preciarLineas, D-50/D-60/D-14).
// Este módulo solo guarda la intención y arma el cuerpo del pedido.
import type {
  CambioVendedor,
  Canal,
  CargoEntrada,
  ClienteEncontrado,
  CondicionPago,
  CrearNota,
  DocumentoNota,
  DocumentoSolicitado,
  EntregaNota,
  EditarNota,
  Nota,
  PrevisualizarNota,
} from '../api/contratos';
import { entradaDecimal, sinCerosDecimales } from '../comun/formato';
import type { Equivalencia } from './cantidad';

export interface LineaBorrador {
  clave: string;
  productoId: string;
  sku: string;
  nombre: string;
  presentacionId: string;
  /** Texto tal como se digitó ("1,5"); se valida al enviar. */
  cantidad: string;
  precioManual: { precio: string; motivo: string } | null;
  descuento: { tipo: 'PORCENTAJE' | 'MONTO'; valor: string } | null;
  /** Ayuda de digitación: lo que se digitó en otra presentación («5 CJ × 9»). No viaja al backend. */
  equivalencia?: Equivalencia | null;
  /**
   * Lista propia de la línea (pedido de Rodrigo, 2026-10-09). Sin ella, sigue a la de la nota. Al
   * cambiar la lista de la nota, las líneas ya digitadas quedan fijas en la anterior; «Recalcular»
   * las vuelve a la de la nota.
   */
  listaPrecioId?: string;
}

export interface ClienteNota {
  id: string;
  rut: string | null;
  razonSocial: string;
  condicionPago: string | null;
  bloqueado: boolean;
  estado: string;
  /** El documento que la ficha del cliente dice emitirle (null = el del canal). */
  documentoPorDefecto?: DocumentoSolicitado | null;
}

export interface Borrador {
  notaId: string | null;
  version: number | null;
  cliente: ClienteNota | null;
  canal: Canal;
  ubicacionId: string;
  /** '' = el documento por defecto de la política del canal (D-113). */
  documento: DocumentoNota | '';
  /** Despacho o retiro en el local; '' = según el canal (el mesón retira). */
  entrega: EntregaNota | '';
  /** Día comprometido de entrega ('' = el mismo de la nota). */
  fechaEntrega: string;
  /** Venta directa (punto de venta, factura o boleta directa): no es un pedido (2026-10-23). */
  ventaDirecta?: boolean;
  /** 0107: «el cliente cancela la factura anterior a camión»: lo pendiente se cobra al entregar. */
  cobroContraEntrega?: boolean;
  /** '' = la condición del maestro del cliente. */
  condicionPago: CondicionPago | '';
  plazoDias: string;
  /** '' = el vendedor de la relación comercial. Otro exige ventas.nota.cambiar_vendedor (backend). */
  vendedorId: string;
  /** Motivo del cambio de vendedor (lo pide el backend si no es el del cliente). */
  cambioVendedor: CambioVendedor | null;
  /** '' = la lista del cliente. Otra (permitida) exige ventas.nota.cambiar_lista_precio (backend). */
  listaPrecioId: string;
  observaciones: string;
  flete: { bruto: string; motivo: string } | null;
  /** Una de las direcciones de despacho del cliente ('' = sin dirección elegida). */
  direccionEntregaId: string;
  lineas: LineaBorrador[];
  /** Fecha de negocio de una nota ya guardada (la previsualización precia a esa fecha). */
  fechaNegocio: string | null;
  /** Cambios locales que todavía no se GUARDARON (el cálculo es aparte: previsualizar no guarda). */
  sucio: boolean;
  secuencia: number;
}

export function borradorNuevo(canal: Canal = 'VENDEDOR', ubicacionId = ''): Borrador {
  return {
    notaId: null,
    version: null,
    cliente: null,
    canal,
    ubicacionId,
    documento: '',
    entrega: '',
    fechaEntrega: '',
    condicionPago: '',
    plazoDias: '',
    vendedorId: '',
    cambioVendedor: null,
    listaPrecioId: '',
    observaciones: '',
    flete: null,
    direccionEntregaId: '',
    lineas: [],
    fechaNegocio: null,
    sucio: false,
    secuencia: 1,
  };
}

export const clienteDesdeBusqueda = (c: ClienteEncontrado): ClienteNota => ({
  id: c.id,
  rut: c.rut ? `${c.rut}-${c.dv ?? ''}` : null,
  razonSocial: c.razon_social,
  condicionPago: c.condicion_pago,
  bloqueado: !!c.bloqueado,
  estado: c.estado,
  documentoPorDefecto: (c.documento_por_defecto as DocumentoSolicitado | null | undefined) ?? null,
});

/** Una nota existente (del backend) vuelve a ser borrador editable con SUS líneas. */
export function borradorDesdeNota(n: Nota): Borrador {
  const flete = n.cargos.find((c) => c.tipo === 'FLETE_VENTA');
  return {
    notaId: n.notaId,
    version: n.version,
    cliente: {
      id: n.cabecera.clienteEntidadId,
      rut: n.cabecera.clienteRut,
      razonSocial: n.cabecera.clienteRazonSocial,
      condicionPago: n.cabecera.condicionPago,
      bloqueado: false,
      estado: 'activa',
    },
    canal: n.cabecera.canal,
    ubicacionId: n.cabecera.ubicacionId,
    documento: n.cabecera.documentoSolicitado,
    entrega: n.entrega ?? 'DESPACHO',
    fechaEntrega: n.fechaEntrega ?? '',
    ...(n.ventaDirecta ? { ventaDirecta: true } : {}),
    ...(n.cobroContraEntrega ? { cobroContraEntrega: true } : {}),
    condicionPago: n.cabecera.condicionPago,
    plazoDias: String(n.cabecera.plazoDias ?? ''),
    vendedorId: n.cabecera.vendedorId ?? '',
    cambioVendedor: null,
    listaPrecioId: n.cabecera.listaPrecioId ?? '',
    observaciones: n.cabecera.observaciones ?? '',
    flete: flete ? { bruto: flete.bruto, motivo: flete.motivo ?? 'flete' } : null,
    direccionEntregaId: n.cabecera.direccionEntregaId ?? '',
    lineas: n.lineas.map((l) => ({
      clave: `b${l.numero}`,
      productoId: l.productoId,
      sku: l.sku,
      nombre: l.descripcion,
      presentacionId: l.presentacionId,
      cantidad: sinCerosDecimales(l.cantidad),
      precioManual:
        l.origenPrecio === 'MANUAL' && l.precioAplicado
          ? { precio: sinCerosDecimales(l.precioAplicado), motivo: l.motivoPrecio ?? '' }
          : null,
      descuento:
        l.descuentoTipo && l.descuentoValor
          ? { tipo: l.descuentoTipo, valor: l.descuentoValor }
          : null,
      ...(l.listaPrecioId && l.listaPrecioId !== n.cabecera.listaPrecioId
        ? { listaPrecioId: l.listaPrecioId }
        : {}),
    })),
    fechaNegocio: n.cabecera.fechaNegocio,
    sucio: false,
    secuencia: n.lineas.length + 1,
  };
}

export type AccionBorrador =
  | { tipo: 'cliente'; cliente: ClienteNota }
  | {
      tipo: 'cabecera';
      campos: Partial<
        Pick<
          Borrador,
          | 'canal'
          | 'ubicacionId'
          | 'documento'
          | 'entrega'
          | 'fechaEntrega'
          | 'condicionPago'
          | 'plazoDias'
          | 'vendedorId'
          | 'cambioVendedor'
          | 'listaPrecioId'
          | 'observaciones'
          | 'flete'
          | 'direccionEntregaId'
          | 'cobroContraEntrega'
        >
      >;
    }
  | { tipo: 'agregar'; linea: Omit<LineaBorrador, 'clave'> }
  | { tipo: 'linea'; clave: string; campos: Partial<Omit<LineaBorrador, 'clave'>> }
  | { tipo: 'quitar'; clave: string }
  /** Otra lista para la nota: lo ya digitado queda con la lista que tenía (`anterior`). */
  | { tipo: 'cambiarLista'; listaPrecioId: string; anterior: string | null }
  /** Todas las líneas pasan a la lista de la nota. */
  | { tipo: 'recalcularLista' }
  | { tipo: 'guardado'; notaId: string; version: number }
  | { tipo: 'cargar'; borrador: Borrador };

export function reducirBorrador(b: Borrador, a: AccionBorrador): Borrador {
  switch (a.tipo) {
    case 'cliente':
      return { ...b, cliente: a.cliente, sucio: true };
    case 'cabecera':
      return { ...b, ...a.campos, sucio: true };
    case 'agregar':
      return {
        ...b,
        lineas: [...b.lineas, { ...a.linea, clave: `n${b.secuencia}` }],
        secuencia: b.secuencia + 1,
        sucio: true,
      };
    case 'linea':
      return {
        ...b,
        lineas: b.lineas.map((l) => (l.clave === a.clave ? { ...l, ...a.campos } : l)),
        sucio: true,
      };
    case 'quitar':
      return { ...b, lineas: b.lineas.filter((l) => l.clave !== a.clave), sucio: true };
    case 'cambiarLista':
      return {
        ...b,
        listaPrecioId: a.listaPrecioId,
        lineas: b.lineas.map((l) => {
          const propia = l.listaPrecioId ?? a.anterior ?? undefined;
          // La que coincide con la nueva lista de la nota vuelve a seguirla.
          if (!propia || propia === a.listaPrecioId) {
            const { listaPrecioId: _, ...resto } = l;
            return resto;
          }
          return { ...l, listaPrecioId: propia };
        }),
        sucio: true,
      };
    case 'recalcularLista':
      return {
        ...b,
        lineas: b.lineas.map(({ listaPrecioId: _, ...resto }) => resto),
        sucio: true,
      };
    case 'guardado':
      return { ...b, notaId: a.notaId, version: a.version, sucio: false };
    case 'cargar':
      return a.borrador;
  }
}

export interface ProblemaBorrador {
  campo: string;
  mensaje: string;
}

/** Lo mínimo para ENVIAR (forma, no reglas de negocio: esas las decide el backend). */
export function problemasDe(b: Borrador): ProblemaBorrador[] {
  const p: ProblemaBorrador[] = [];
  if (!b.cliente) p.push({ campo: 'cliente', mensaje: 'Falta el cliente (F2)' });
  if (!b.ubicacionId) p.push({ campo: 'ubicacion', mensaje: 'Falta la ubicación de venta' });
  if (!b.lineas.length) p.push({ campo: 'lineas', mensaje: 'Agregue al menos una línea (F3)' });
  b.lineas.forEach((l, i) => {
    if (!entradaDecimal(l.cantidad))
      p.push({ campo: `cantidad-${l.clave}`, mensaje: `Línea ${i + 1}: cantidad inválida` });
    if (l.precioManual) {
      if (!entradaDecimal(l.precioManual.precio, 4))
        p.push({ campo: `precio-${l.clave}`, mensaje: `Línea ${i + 1}: precio manual inválido` });
      if (l.precioManual.motivo.trim().length < 3)
        p.push({
          campo: `motivo-${l.clave}`,
          mensaje: `Línea ${i + 1}: el precio manual exige motivo`,
        });
    }
    if (l.descuento && !entradaDecimal(l.descuento.valor, 2))
      p.push({ campo: `descuento-${l.clave}`, mensaje: `Línea ${i + 1}: descuento inválido` });
  });
  if (b.flete) {
    if (!entradaDecimal(b.flete.bruto, 0))
      p.push({ campo: 'flete', mensaje: 'Flete: monto en pesos enteros' });
    if (b.flete.motivo.trim().length < 3)
      p.push({ campo: 'flete', mensaje: 'Flete: falta el motivo' });
  }
  if (b.plazoDias && !/^\d{1,3}$/.test(b.plazoDias))
    p.push({ campo: 'plazo', mensaje: 'Plazo en días (0 a 365)' });
  return p;
}

function lineasDe(b: Borrador): CrearNota['lineas'] {
  return b.lineas.map((l) => ({
    productoId: l.productoId,
    presentacionId: l.presentacionId,
    cantidad: entradaDecimal(l.cantidad)!,
    ...(l.precioManual
      ? {
          precioManual: {
            precio: entradaDecimal(l.precioManual.precio, 4)!,
            motivo: l.precioManual.motivo.trim(),
          },
        }
      : {}),
    ...(l.descuento
      ? { descuento: { tipo: l.descuento.tipo, valor: entradaDecimal(l.descuento.valor, 2)! } }
      : {}),
    ...(l.listaPrecioId ? { listaPrecioId: l.listaPrecioId } : {}),
  }));
}

function cargosDe(b: Borrador): CargoEntrada[] {
  return b.flete
    ? [
        {
          tipo: 'FLETE_VENTA',
          descripcion: 'Flete de venta',
          bruto: entradaDecimal(b.flete.bruto, 0)!,
          motivo: b.flete.motivo.trim(),
        },
      ]
    : [];
}

export function cuerpoCrear(b: Borrador, empresaId: string): CrearNota {
  return {
    empresaId,
    clienteEntidadId: b.cliente!.id,
    canal: b.canal,
    ubicacionId: b.ubicacionId,
    ...(b.documento ? { documentoSolicitado: b.documento } : {}),
    ...(b.condicionPago ? { condicionPago: b.condicionPago } : {}),
    ...(b.plazoDias ? { plazoDias: Number.parseInt(b.plazoDias, 10) } : {}),
    ...(b.vendedorId ? { vendedorId: b.vendedorId } : {}),
    ...(b.cambioVendedor ? { cambioVendedor: b.cambioVendedor } : {}),
    ...(b.listaPrecioId ? { listaPrecioId: b.listaPrecioId } : {}),
    ...(b.observaciones.trim() ? { observaciones: b.observaciones.trim() } : {}),
    ...(b.direccionEntregaId ? { direccionEntregaId: b.direccionEntregaId } : {}),
    ...(b.entrega ? { entrega: b.entrega } : {}),
    ...(b.fechaEntrega ? { fechaEntrega: b.fechaEntrega } : {}),
    ...(b.ventaDirecta ? { ventaDirecta: true } : {}),
    ...(b.cobroContraEntrega ? { cobroContraEntrega: true } : {}),
    lineas: lineasDe(b),
    cargos: cargosDe(b),
  };
}

/**
 * El cuerpo de PREVISUALIZAR: exactamente lo que se enviaría al crear (más la fecha de negocio de una
 * nota ya guardada). El backend lo precia con las mismas reglas que al confirmar, sin guardar nada.
 * null si la forma aún no está completa (cliente, ubicación, líneas válidas).
 */
export function cuerpoPrevisualizar(b: Borrador, empresaId: string): PrevisualizarNota | null {
  if (problemasDe(b).length) return null;
  return {
    ...cuerpoCrear(b, empresaId),
    ...(b.fechaNegocio ? { fechaNegocio: b.fechaNegocio } : {}),
  };
}

export function cuerpoEditar(b: Borrador): EditarNota {
  return {
    version: b.version!,
    ubicacionId: b.ubicacionId,
    ...(b.documento ? { documentoSolicitado: b.documento } : {}),
    ...(b.condicionPago ? { condicionPago: b.condicionPago } : {}),
    ...(b.plazoDias ? { plazoDias: Number.parseInt(b.plazoDias, 10) } : {}),
    ...(b.vendedorId ? { vendedorId: b.vendedorId } : {}),
    ...(b.cambioVendedor ? { cambioVendedor: b.cambioVendedor } : {}),
    ...(b.listaPrecioId ? { listaPrecioId: b.listaPrecioId } : {}),
    observaciones: b.observaciones.trim() || null,
    direccionEntregaId: b.direccionEntregaId || null,
    ...(b.entrega ? { entrega: b.entrega } : {}),
    fechaEntrega: b.fechaEntrega || null,
    cobroContraEntrega: !!b.cobroContraEntrega,
    lineas: lineasDe(b),
    cargos: cargosDe(b),
  };
}
